// src/pages/OrganizePage.tsx
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { authStorage, dashboardStorage } from '@/lib/storage';
import TabControl from '@/components/TabControl/TabControl';
import { FilterBar } from '@/components/Filter/FilterBar';
import { SearchBar } from '@/components/Filter/SearchBar';
import { DashboardGroups } from '@/components/DashboardGroups/DashboardGroups';
import {
  DashboardViewSelector,
  type DashboardViewType,
} from '@/components/Dashboard/DashboardViewSelector';
import { GridView } from '@/components/GridView/GridView';
import type { GridViewRef } from '@/components/GridView/types';
import { PhaseDetails } from '@/components/PhaseDetails/PhaseDetails';
import type { PhaseDetailsRef } from '@/components/PhaseDetails/types';
import { PhaseDetailsV2 } from '@/components/PhaseDetailsV2/PhaseDetailsV2';
import type { PhaseDetailsV2Ref } from '@/components/PhaseDetailsV2/types';
import { CalendarView } from '@/components/CalendarView';
import { GalleryView } from '@/components/Dashboard/GalleryView';
import { FilesView } from '@/components/Dashboard/FilesView';
import Gantt from '@/components/Gantt/Gantt';
import type { GanttConfig } from '@/components/Gantt/Gantt';
import { useInfoWindow } from '@/contexts/InfoWindowContext';
import { filterService } from '@/services/filterService';
import { dashboardApi, MIN_COLUMN_WIDTH_PX, gridSizeToColumnWidth } from '@/services/dashboardApi';
import { useDashboardViewSync } from '@/hooks/useDashboardViewSync';
import { config } from '@config/env';
import { useFilterPersistence } from '@/hooks/useFilterPersistence';
import type { TabControlConfig, ReactFunctions, Dashboard } from '@/components/TabControl/types';
import type { FilterOption } from '@/types/dashboard';
import type { DashboardGroupData } from '@/components/DashboardGroups/types';
import type { ConversationDetail } from '@/types/conversation';
import { searchHistory } from '@/lib/searchHistory';

export const OrganizePage: React.FC = () =>
{
  const { dashboardId: dashboardIdParam } = useParams<{ dashboardId: string }>();
  const dashboardId = parseInt(dashboardIdParam || '0', 10);
  const configValueId = 1;

  const { IsAdmin } = useAuth();

  const [currentDashboard, setCurrentDashboard] = useState<Dashboard | null>(null);

  const isDark = document.documentElement.classList.contains('dark');

  const {
    filtersSelected,
    appliedFilters,
    handleFilterSelect,
    handleFilterUnselect,
    handleApplyFilters,
  } = useFilterPersistence(`C2_Filters_Organize_${dashboardId}`);

  const [isApplyForAll, setIsApplyForAll] = useState(false);

  const handleApplyFiltersWrapper = useCallback((applyForAll: boolean) =>
  {
    setIsApplyForAll(applyForAll);
    handleApplyFilters();
  }, [handleApplyFilters]);

  const extraFilterParams = useMemo(() =>
  {
    const extra: Record<string, any> = {};
    const customFields: any[] = [];
    appliedFilters.forEach(f =>
    {
      if (f.IsCustomField)
      {
        customFields.push({
          Name: f.FieldName,
          FieldTypeId: f.FieldTypeId,
          Value: f.Id,
          ComparisonOperator: f.ComparisonOperator || null,
        });
      }
      else if (f.Id === '0' || f.Id === '1')
      {
        extra[f.FieldName] = parseInt(f.Id, 10);
      }
    });
    if (customFields.length > 0) extra.CustomFields = customFields;
    extra.IsFilterPersonal = !isApplyForAll;
    return extra;
  }, [appliedFilters, isApplyForAll]);

  const sessionLayersToHide = useMemo(() =>
  {
    try
    {
      const arr = JSON.parse(sessionStorage.getItem('C2_LayersToHide') || '[]');
      return arr.length ? arr.join(',') : '';
    }
    catch
    {
      return '';
    }
  }, []);

  const filtersJson = useMemo(() =>
  {
    const allFilters = appliedFilters.map(f => ({
      Name: f.FieldName,
      IsCustomField: f.IsCustomField,
      FieldTypeId: f.FieldTypeId,
      CustomFields: f.CustomFields || null,
      ComparisonOperator: f.ComparisonOperator || null,
      isChipFilter: null,
      isSelected: true,
      Text: f.Name,
      Value: f.Id,
    }));
    return JSON.stringify(allFilters);
  }, [appliedFilters]);

  const [filter, setFilter] = useState<string>('');
  const [filterOptions, setFilterOptions] = useState<FilterOption[]>([]);
  const [filterLoading, setFilterLoading] = useState(false);
  const [filterError, setFilterError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState<string>('');
  const [columnWidth, setColumnWidth] = useState(350);
  const [dashboardViewTypeId, setDashboardViewTypeId] = useState<DashboardViewType>(1);
  const { persistDashboardView, persistDashboardViewDebounced } = useDashboardViewSync();
  const [pageSize] = useState(130);
  const [showAllGalleryImages, setShowAllGalleryImages] = useState(false);

  const cardViewMode: 'compact' | 'detailed' = dashboardViewTypeId === 2 ? 'detailed' : 'compact';

  const [enableWeekend, setEnableWeekend] = useState(false);
  const [selectedLayerId, setSelectedLayerId] = useState(0);
  const [ganttLayers, setGanttLayers] = useState<{ LayerId: number; Name: string }[]>([]);

  const reactFunctionsRef = useRef<ReactFunctions | null>(null);
  const gridExportRef = useRef<GridViewRef>(null);
  const phaseDetailsExportRef = useRef<PhaseDetailsRef>(null);
  const phaseDetailsV2ExportRef = useRef<PhaseDetailsV2Ref>(null);

  // Refs to hold latest view/grid without stale closures
  const dashboardViewTypeIdRef = useRef<DashboardViewType>(dashboardViewTypeId);
  useEffect(() =>
  {
    dashboardViewTypeIdRef.current = dashboardViewTypeId;
  }, [dashboardViewTypeId]);
  const columnWidthRef = useRef<number>(columnWidth);
  useEffect(() =>
  {
    columnWidthRef.current = columnWidth;
  }, [columnWidth]);

  const { openInfoWindow } = useInfoWindow();

  const [viewSyncDashboardId, setViewSyncDashboardId] = useState<number | null>(null);

  // Resolve view type and grid size on dashboard switch
  // PRIORITY: localStorage stores user's last visited view per dashboard
  if (currentDashboard && currentDashboard.DashboardId !== viewSyncDashboardId)
  {
    setViewSyncDashboardId(currentDashboard.DashboardId);

    let newViewId: DashboardViewType = 1;
    let newGridSize = 350;

    const savedEntry = dashboardStorage.findByContext(
      0,
      configValueId,
      currentDashboard.DashboardId,
    );

    if (savedEntry?.GridSize)
    {
      newGridSize = Math.max(MIN_COLUMN_WIDTH_PX, savedEntry.GridSize);
    }
    else if (currentDashboard.GridSize)
    {
      newGridSize = gridSizeToColumnWidth(currentDashboard.GridSize);
    }

    if (savedEntry?.DashboardViewId)
    {
      // Use saved view from localStorage (user's last visit)
      newViewId = savedEntry.DashboardViewId as DashboardViewType;
    }
    else
    {
      // First-time visit: default to compact view (small cards)
      newViewId = 1;
    }

    setColumnWidth(newGridSize);
    setDashboardViewTypeId(newViewId);
  }

  // Reset state when dashboardId changes
  useEffect(() =>
  {
    setCurrentDashboard(null);
    setFilter('');
    setSearchText('');
  }, [dashboardId]);

  const getApiConfig = useCallback(
    () => ({
      url: config.api.baseUrl,
      token: authStorage.getAccessToken() || '',
      refreshToken: '',
      TenancyName: config.tenant.defaultName,
    }),
    [],
  );

  // True once the user has typed in the filter input
  const filterTouchedRef = useRef<boolean>(false);

  useEffect(() =>
  {
    const loadFilterOptions = async () =>
    {
      if (filter.length > 0) filterTouchedRef.current = true;
      // Below the search threshold, reload the full list instead of clearing it
      const searchTerm = filter.length >= 2 ? filter : null;
      // On first mount the full list is loaded by onFilterOpened instead
      if (!searchTerm && !filterTouchedRef.current) return;
      try
      {
        setFilterLoading(true);
        setFilterError(null);
        const options = await filterService.getFilterOptions(searchTerm, {
          IsFromProject: false,
          ConfigValueId: configValueId,
        });
        setFilterOptions(options);
      }
      catch
      {
        setFilterError('Error loading filters');
      }
      finally
      {
        setFilterLoading(false);
      }
    };
    const t = setTimeout(loadFilterOptions, 300);
    return () => clearTimeout(t);
  }, [filter]);

  const handleFilterOpened = useCallback(async () =>
  {
    try
    {
      setFilterLoading(true);
      setFilterError(null);
      const options = await filterService.getFilterOptions(null, {
        IsFromProject: false,
        ConfigValueId: configValueId,
      });
      setFilterOptions(options);
    }
    catch
    {
      setFilterError('Error loading filters');
    }
    finally
    {
      setFilterLoading(false);
    }
  }, []);

  const saveDashboardState = useCallback(
    (dashboard: Dashboard, viewId: DashboardViewType, gridSize: number) =>
    {
      dashboardStorage.updateDashboard({
        projectId: 0,
        configValueId: configValueId,
        index: 0,
        dashboardId: dashboard.DashboardId,
        DashboardViewId: viewId,
        GridSize: gridSize,
        lastModified: Date.now(),
      });
    },
    [configValueId],
  );

  // Save the current dashboard selection to C2_currentDashboard
  useEffect(() =>
  {
    if (!currentDashboard) return;
    saveDashboardState(currentDashboard, dashboardViewTypeIdRef.current, columnWidthRef.current);
  }, [currentDashboard, saveDashboardState]);

  // Apply persisted view and grid size to local state
  const applyDashboardView = useCallback(
    (dashboardId: number, viewType: DashboardViewType, apiGridSize: number) =>
    {
      setCurrentDashboard(prev => prev && prev.DashboardId === dashboardId ? {
        ...prev,
        DashboardViewTypeId: viewType,
        GridSize: apiGridSize,
      } : prev);

      reactFunctionsRef.current?.updateDashboardView(dashboardId, viewType, apiGridSize);
    },
    [],
  );

  const handleViewChange = useCallback(
    (viewType: DashboardViewType) =>
    {
      setDashboardViewTypeId(viewType);
      if (!currentDashboard) return;
      const gridSize = columnWidthRef.current;
      saveDashboardState(currentDashboard, viewType, gridSize);

      persistDashboardView(currentDashboard.DashboardId, viewType, gridSize, apiGridSize =>
      {
        applyDashboardView(currentDashboard.DashboardId, viewType, apiGridSize);
      });
    },
    [currentDashboard, saveDashboardState, persistDashboardView, applyDashboardView],
  );

  // Persist column resize (debounced)
  const handleColumnWidthChange = useCallback(
    (newWidth: number) =>
    {
      setColumnWidth(newWidth);
      if (!currentDashboard) return;

      const viewType = dashboardViewTypeIdRef.current;
      saveDashboardState(currentDashboard, viewType, newWidth);

      persistDashboardViewDebounced(
        currentDashboard.DashboardId,
        viewType,
        newWidth,
        apiGridSize =>
        {
          applyDashboardView(currentDashboard.DashboardId, viewType, apiGridSize);
        },
      );
    },
    [currentDashboard, saveDashboardState, persistDashboardViewDebounced, applyDashboardView],
  );

  useEffect(() =>
  {
    document.title = currentDashboard?.Name || 'Organize';
  }, [currentDashboard]);

  useEffect(() =>
  {
    if (currentDashboard?.DashboardId && currentDashboard?.Name)
    {
      searchHistory.addDashboardVisit(
        currentDashboard.DashboardId,
        currentDashboard.Name,
        null,
        'ORGANIZE',
      );
    }
  }, [currentDashboard]);

  const handleSetDashboards = useCallback(
    (
      _all: Dashboard[],
      _pin: Dashboard[],
      _notPin: Dashboard[],
      current: Dashboard | undefined,
    ) =>
    {
      if (current) setCurrentDashboard(current);
    },
    [],
  );

  const handleSetDashboard = useCallback((dashboard: Dashboard) =>
  {
    setCurrentDashboard(dashboard);
    setSearchText('');
  }, []);

  const handleUpdateDashboard = useCallback(async (_e: React.MouseEvent, dashboard: Dashboard) =>
  {
    try
    {
      const updated = await dashboardApi.updateDashboard(dashboard);
      setCurrentDashboard(prev =>
        prev?.DashboardId === updated.DashboardId ? { ...prev, ...updated } : prev,
      );
      window.dispatchEvent(new CustomEvent('organize:refresh'));
    }
    catch (error)
    {
      console.error('Error updating organize dashboard:', error);
    }
  }, []);

  const handleOpenTabDashboard = useCallback((_e: React.MouseEvent, dashboard: Dashboard) =>
  {
    setCurrentDashboard(dashboard);
  }, []);

  const attachReactFunctions = useCallback((functions: ReactFunctions) =>
  {
    reactFunctionsRef.current = functions;
  }, []);

  const tabControlConfig: TabControlConfig = {
    api: getApiConfig(),
    configValueId,
    projectId: undefined,
    DashboardId: dashboardId,
    isOrganize: true,
    isBookmarks: false,
    isProjectDetail: false,
    isDashboardShared: false,
    recycleBinDashboard: false,
    canEdit: true,
    isAdmin: IsAdmin(),
    webUrl: import.meta.env.VITE_WEB_URL || 'http://localhost/Corgee.WebClient/',
    functions: {
      setDashboards: handleSetDashboards,
      setDashboard: handleSetDashboard,
      addDashboard: () => {},
      updateDashboard: handleUpdateDashboard,
      pinDashboard: () => {},
      deleteDashboard: () => {},
      OpenTabDashboard: handleOpenTabDashboard,
      attachReactFunctions,
    },
  };

  const buildSearchText = useCallback(() =>
  {
    return searchText.trim();
  }, [searchText]);

  return (
    <div className="organize-page h-full flex flex-col">
      <div className="border border-gray-200 rounded-lg bg-gray-50">
        <TabControl key={dashboardId} config={tabControlConfig} />
      </div>

      <div className="flex items-center justify-between gap-2" style={{ paddingLeft: '15px' }}>
        <div className="flex items-center gap-2">
          <FilterBar
            filter={filter}
            onChangeFilter={setFilter}
            loading={filterLoading}
            filterOptions={filterOptions}
            error={filterError}
            filtersSelected={filtersSelected}
            onFilterOpened={handleFilterOpened}
            onFilterSelect={handleFilterSelect}
            onFilterUnselect={handleFilterUnselect}
            onApplyFilters={handleApplyFiltersWrapper}
          />
          <SearchBar key={dashboardId} filterSearchCard={searchText} onChangeSearchFilterCards={setSearchText} />
        </div>
        <div className="flex items-center gap-1">
          {dashboardViewTypeId !== 3 &&
            dashboardViewTypeId !== 4 &&
            dashboardViewTypeId !== 5 &&
            dashboardViewTypeId !== 6 &&
            dashboardViewTypeId !== 7 &&
            dashboardViewTypeId !== 9 &&
            dashboardViewTypeId !== 8 && (
            <input
              type="range"
              min="250"
              max="800"
              value={columnWidth}
              onChange={e => handleColumnWidthChange(Number(e.target.value))}
              className="w-24 h-1 custom-slider"
              style={{ accentColor: '#62aacb' }}
            />
          )}
          {dashboardViewTypeId === 6 && (
            <label
              className="flex items-center gap-1 text-xs cursor-pointer"
              style={{
                color: isDark ? '#6e6860' : 'rgba(0,0,0,0.6)',
                fontSize: '10px',
                paddingRight: '15px',
                paddingTop: '7px',
              }}
            >
              <input
                type="checkbox"
                checked={showAllGalleryImages}
                onChange={e => setShowAllGalleryImages(e.target.checked)}
                className="w-3 h-3"
              />
              Show All Attached Images
            </label>
          )}
          {dashboardViewTypeId === 7 && (
            <>
              <label
                className="flex items-center gap-1 text-xs cursor-pointer"
                style={{
                  color: isDark ? '#6e6860' : 'rgba(0,0,0,0.6)',
                  fontSize: '10px',
                  paddingRight: '15px',
                  paddingTop: '7px',
                }}
              >
                <input
                  type="checkbox"
                  checked={enableWeekend}
                  onChange={e => setEnableWeekend(e.target.checked)}
                  className="w-3 h-3"
                />
                Enable Weekend
              </label>
              <select
                className="text-xs border rounded px-1 py-0.5"
                style={{
                  color: isDark ? '#a9a093' : 'rgba(0,0,0,0.7)',
                  backgroundColor: isDark ? '#3a3c3c' : undefined,
                  borderColor: isDark ? '#4a4c4c' : undefined,
                }}
                value={selectedLayerId}
                onChange={e => setSelectedLayerId(Number(e.target.value))}
              >
                {ganttLayers.map(layer => (
                  <option key={layer.LayerId} value={layer.LayerId}>
                    {layer.Name}
                  </option>
                ))}
              </select>
            </>
          )}
          {(dashboardViewTypeId === 4 || dashboardViewTypeId === 11) && (
            <button
              className="view-bar-btn"
              title="Export to Excel"
              onClick={() => gridExportRef.current?.exportToExcel()}
            >
              <i className="material-icons">cloud_download</i>
              <span>Export</span>
            </button>
          )}
          {dashboardViewTypeId === 9 && (
            <>
              <div
                style={{ lineHeight: '24px', color: 'rgba(0, 0, 0, 0.45)' }}
                className="view-bar-button enable-help-tooltip"
                ht-name="Hierarchical cards are not shown in this view"
              >
                <i className="fa fa-exclamation-circle fa-2"></i>
              </div>
              <button
                className="view-bar-btn"
                title="Export to Excel"
                onClick={() => phaseDetailsExportRef.current?.exportToExcel()}
              >
                <i className="material-icons">cloud_download</i>
                <span>Export</span>
              </button>
            </>
          )}
          {dashboardViewTypeId === 8 && (
            <button
              className="view-bar-btn"
              title="Export to Excel"
              onClick={() => phaseDetailsV2ExportRef.current?.exportToExcel()}
            >
              <i className="material-icons">cloud_download</i>
              <span>Export</span>
            </button>
          )}
          <DashboardViewSelector
            currentViewTypeId={dashboardViewTypeId}
            onViewChange={handleViewChange}
          />
        </div>
      </div>

      {currentDashboard && dashboardViewTypeId === 3 && (
        <div className="mt-2">
          <CalendarView
            key={`cal-organize-${currentDashboard.DashboardId}`}
            dashboardId={currentDashboard.DashboardId}
            configValueId={configValueId}
            filters={filtersJson}
            searchText={buildSearchText()}
            layersToHide={sessionLayersToHide}
            extraFilterParams={extraFilterParams}
            onOpenInfoWindow={id => openInfoWindow(id)}
          />
        </div>
      )}

      {currentDashboard && dashboardViewTypeId === 4 && (
        <div className="flex-1 min-h-0 overflow-hidden mt-2">
          <GridView
            ref={gridExportRef}
            key={`grid-organize-${currentDashboard.DashboardId}`}
            dashboardId={currentDashboard.DashboardId}
            dashboardName={currentDashboard.Name}
            filters={filtersJson}
            searchText={buildSearchText()}
            extraFilterParams={extraFilterParams}
            isActive={true}
            onOpenInfoWindow={(conversation: ConversationDetail) =>
              openInfoWindow(conversation.ConversationId, undefined, conversation)
            }
          />
        </div>
      )}

      {currentDashboard && dashboardViewTypeId === 9 && (
        <div className="flex-1 min-h-0 overflow-hidden mt-2">
          <PhaseDetails
            ref={phaseDetailsExportRef}
            key={`phase-organize-${currentDashboard.DashboardId}`}
            dashboardId={currentDashboard.DashboardId}
            dashboardName={currentDashboard.Name}
            filters={filtersJson}
            searchText={buildSearchText()}
            extraFilterParams={extraFilterParams}
            isActive={true}
            onOpenInfoWindow={(conversation: ConversationDetail) =>
              openInfoWindow(conversation.ConversationId, undefined, conversation)
            }
          />
        </div>
      )}

      {currentDashboard && dashboardViewTypeId === 8 && (
        <div className="flex-1 min-h-0 overflow-hidden mt-2">
          <PhaseDetailsV2
            ref={phaseDetailsV2ExportRef}
            key={`phase-v2-organize-${currentDashboard.DashboardId}`}
            dashboardId={currentDashboard.DashboardId}
            dashboardName={currentDashboard.Name}
            filters={filtersJson}
            searchText={buildSearchText()}
            extraFilterParams={extraFilterParams}
            isActive={true}
            onOpenInfoWindow={(conversation: ConversationDetail) =>
              openInfoWindow(conversation.ConversationId, undefined, conversation)
            }
          />
        </div>
      )}

      {currentDashboard && dashboardViewTypeId === 5 && (
        <div className="mt-2">
          <FilesView
            key={`files-organize-${currentDashboard.DashboardId}`}
            dashboardId={currentDashboard.DashboardId}
            projectId=""
            filters={filtersJson}
            searchText={buildSearchText()}
            extraFilterParams={extraFilterParams}
            onOpenInfoWindow={id => openInfoWindow(id)}
          />
        </div>
      )}

      {currentDashboard && dashboardViewTypeId === 6 && (
        <div className="mt-2">
          <GalleryView
            key={`gallery-organize-${currentDashboard.DashboardId}`}
            dashboardId={currentDashboard.DashboardId}
            projectId=""
            filters={filtersJson}
            searchText={buildSearchText()}
            extraFilterParams={extraFilterParams}
            onOpenInfoWindow={id => openInfoWindow(id)}
            showAllImages={showAllGalleryImages}
          />
        </div>
      )}

      {currentDashboard && dashboardViewTypeId === 7 && (
        <div className="gantt-wrapper gantt-container">
          <Gantt
            key={`gantt-organize-${currentDashboard.DashboardId}-layer${selectedLayerId}-weekend${enableWeekend}-${filtersJson}-${buildSearchText()}`}
            dashboard={{
              DashboardId: currentDashboard.DashboardId,
              DashboardViewTypeId: 7,
              LayersToHide: sessionLayersToHide || (currentDashboard as any).LayersToHide || '',
              PageSize: 40,
              PageNumber: 1,
              Filters: filtersJson,
              SearchText: buildSearchText(),
              Project: '',
              ...extraFilterParams,
            }}
            config={
              {
                apiUrl: getApiConfig().url,
                authToken: getApiConfig().token,
                selectedLayerId: selectedLayerId,
                WorkWeekend: enableWeekend,
                openInfoWindow: (conversationId: number) => openInfoWindow(conversationId),
                loadGanttLayers: (layers: any[]) => setGanttLayers(layers),
              } as GanttConfig
            }
          />
        </div>
      )}

      {currentDashboard &&
        dashboardViewTypeId !== 3 &&
        dashboardViewTypeId !== 4 &&
        dashboardViewTypeId !== 5 &&
        dashboardViewTypeId !== 6 &&
        dashboardViewTypeId !== 7 &&
        dashboardViewTypeId !== 9 &&
        dashboardViewTypeId !== 8 && (
        <div className="flex-1 min-h-0 overflow-hidden mt-2">
          <DashboardGroups
            key={`organize-${currentDashboard.DashboardId}`}
            dashboardId={currentDashboard.DashboardId}
            projectId=""
            filters={filtersJson}
            extraFilterParams={extraFilterParams}
            searchText={buildSearchText()}
            pageSize={pageSize}
            cardViewMode={cardViewMode}
            columnWidth={columnWidth}
            dashboardViewTypeId={dashboardViewTypeId}
            configValueId={configValueId}
            onDataLoaded={(_groups: DashboardGroupData[]) => {}}
            onError={(_error: Error) => {}}
            onCardMoved={() => {}}
            onOpenInfoWindow={(conversation: ConversationDetail) =>
              openInfoWindow(conversation.ConversationId, undefined, conversation)
            }
          />
        </div>
      )}
    </div>
  );
};

export default OrganizePage;
